import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload, Trash2, Loader2, ArrowUp, ArrowDown, RefreshCw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { cardImg } from "@/components/TalkingCards";
import { SortableList, SortableItem, PositionInput, moveTo } from "@/components/admin/Sortable";

export const CardsManager = () => {
  const qc = useQueryClient();
  const { data: cards = [] } = useQuery({ queryKey: ["cards"], queryFn: () => api.get("/cards").then((r) => r.data) });
  const fileRef = useRef();
  const replaceRef = useRef();
  const [replacing, setReplacing] = useState(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["cards"] });

  const pickReplacement = (c) => { setReplacing(c); replaceRef.current?.click(); };
  const replace = async (file) => {
    const c = replacing;
    setReplacing(null);
    if (!file || !c) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/cards/${c.id}/replace`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Card replaced — it keeps its spot in the pack");
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      if (replaceRef.current) replaceRef.current.value = "";
    }
  };

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/admin/cards?title=${encodeURIComponent(files.length === 1 ? title : "")}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} card${files.length === 1 ? "" : "s"} uploaded`);
      setTitle("");
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/admin/cards/${c.id}`);
      toast.success("Card removed");
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const reorder = async (ids) => {
    qc.setQueryData(["cards"], (cur = []) => {
      const byId = Object.fromEntries(cur.map((c) => [c.id, c]));
      return ids.map((id, i) => ({ ...byId[id], order: i + 1 }));
    });
    try {
      await api.put("/admin/cards/reorder", { ids });
    } catch (e) {
      toast.error(errMsg(e));
    }
    refresh();
  };
  const move = (i, d) => reorder(moveTo(cards.map((c) => c.id), i, i + d));

  return (
    <div className="space-y-5">
      <div className="rounded-2xl bg-[#1B2A41] border border-[#2C3E55] p-5">
        <h3 className="font-type font-bold text-lg">Talking cards</h3>
        <p className="text-xs text-[#F8FAFC]/50 mt-1">Portrait images (PNG/JPG, A-series 5:7 like the palm cards). They slide under the Documents boxes; visitors can flick through, download one, or download the whole pack as a printable PDF. Drag a card (by its grip) or type a # to set the order; Replace swaps the image without moving it.</p>
        <div className="grid sm:grid-cols-[1fr_auto] gap-3 mt-4">
          <Input data-testid="admin-card-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Caption for a single upload (optional)" className="h-11 bg-[#121E2B] border-[#2C3E55]" />
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="admin-card-file-input" onChange={(e) => upload(Array.from(e.target.files || []))} />
          <input ref={replaceRef} type="file" accept="image/*" className="hidden" data-testid="admin-card-replace-input" onChange={(e) => replace(e.target.files?.[0])} />
          <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="admin-card-upload-button" className="h-11 px-5 rounded-full bg-[#E2705A] hover:bg-[#D35D46] disabled:opacity-60 font-type font-bold uppercase tracking-wider text-xs inline-flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload image(s)
          </button>
        </div>
      </div>
      {cards.length === 0 ? (
        <p className="text-sm text-[#F8FAFC]/50" data-testid="admin-cards-empty">No cards yet — the site shows placeholder tiles until you upload.</p>
      ) : (
        <SortableList items={cards} onReorder={reorder} grid>
          <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="admin-cards-grid">
            {cards.map((c, i) => (
              <SortableItem key={c.id} id={c.id} className="rounded-xl overflow-hidden bg-[#1B2A41] border border-[#2C3E55]">
                {(handle) => (
                  <div data-testid={`admin-card-${c.id}`}>
                    <div className="relative">
                      <img src={cardImg(c, "thumb")} alt="" className="w-full aspect-[5/7] object-cover" />
                      <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-[#121E2B]/80 text-[10px] font-type font-bold">#{i + 1}</span>
                      <span className="absolute top-1 right-1 rounded-md bg-[#121E2B]/80">{handle}</span>
                      <button type="button" onClick={() => pickReplacement(c)} disabled={busy} data-testid={`admin-card-replace-${c.id}`} className="absolute inset-x-2 bottom-2 h-8 rounded-full bg-[#E2705A] hover:bg-[#D35D46] disabled:opacity-60 text-[11px] font-type font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5 shadow-lg">
                        <RefreshCw className="w-3.5 h-3.5" /> Replace
                      </button>
                    </div>
                    <div className="p-2 flex items-center justify-between gap-1 text-xs">
                      <span className="truncate flex-1">{c.title || c.original_filename}</span>
                      <PositionInput index={i} count={cards.length} onMove={(to) => reorder(moveTo(cards.map((x) => x.id), i, to))} testId={`admin-card-position-${c.id}`} />
                      <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 rounded hover:bg-[#2C3E55] disabled:opacity-30" data-testid="admin-card-up"><ArrowUp className="w-3.5 h-3.5" /></button>
                      <button onClick={() => move(i, 1)} disabled={i === cards.length - 1} className="p-1 rounded hover:bg-[#2C3E55] disabled:opacity-30" data-testid="admin-card-down"><ArrowDown className="w-3.5 h-3.5" /></button>
                      <button onClick={() => remove(c)} className="p-1 rounded text-[#F8FAFC]/60 hover:text-[#E2705A]" data-testid="admin-card-delete"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                )}
              </SortableItem>
            ))}
          </div>
        </SortableList>
      )}
    </div>
  );
};
